import { apiClient as api } from './api';
import type { Alert, AlertListResult } from '../types/alert';
import { AlertType, AlertSeverity, AlertStatus } from '../types/alert';

// Backend'den gelen string/number enum değerlerini frontend enum'ına çevir
const mapAlertType = (type: string | number | AlertType): AlertType => {
  if (typeof type === 'number') return type as AlertType;
  if (typeof type === 'string') {
    const num = parseInt(type, 10);
    if (!isNaN(num)) return num as AlertType;
  }
  return type as unknown as AlertType;
};

const mapAlertSeverity = (severity: string | number | AlertSeverity): AlertSeverity => {
  if (typeof severity === 'number') return severity as AlertSeverity;
  if (typeof severity === 'string') {
    const num = parseInt(severity, 10);
    if (!isNaN(num)) return num as AlertSeverity;
  }
  return severity as unknown as AlertSeverity;
};

const mapAlertStatus = (status: string | number | AlertStatus): AlertStatus => {
  if (typeof status === 'number') return status as AlertStatus;
  if (typeof status === 'string') {
    // Önce sayısal değer olup olmadığını kontrol et
    const num = parseInt(status, 10);
    if (!isNaN(num)) return num as AlertStatus;
    
    // String enum isimlerini handle et
    const statusLower = status.toLowerCase();
    if (statusLower === 'new' || statusLower === 'yeni' || statusLower === 'devam ediyor') return AlertStatus.New;
    if (statusLower === 'acknowledged' || statusLower === 'onaylandı') return AlertStatus.Acknowledged;
    if (statusLower === 'resolved' || statusLower === 'çözüldü') return AlertStatus.Resolved;
  }
  return status as unknown as AlertStatus;
};

export const alertService = {
  /**
   * Alert listesini getir
   */
  async getAll(params?: {
    tenantId?: string;
    serverId?: string;
    applicationDefinitionId?: string;
    alertType?: AlertType;
    alertTypes?: AlertType[];
    excludeAlertTypes?: AlertType[];
    severity?: AlertSeverity;
    status?: AlertStatus;
    startDate?: Date;
    endDate?: Date;
    page?: number;
    pageSize?: number;
  }): Promise<AlertListResult> {
    const queryParams = new URLSearchParams();
    
    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params?.serverId) queryParams.append('serverId', params.serverId);
    if (params?.applicationDefinitionId) queryParams.append('applicationDefinitionId', params.applicationDefinitionId);
    if (params?.alertType) queryParams.append('alertType', params.alertType.toString());
    if (params?.alertTypes && params.alertTypes.length > 0) {
      params.alertTypes.forEach(type => {
        queryParams.append('alertTypes', type.toString());
      });
    }
    if (params?.excludeAlertTypes && params.excludeAlertTypes.length > 0) {
      params.excludeAlertTypes.forEach(type => {
        queryParams.append('excludeAlertTypes', type.toString());
      });
    }
    if (params?.severity) queryParams.append('severity', params.severity.toString());
    if (params?.status) queryParams.append('status', params.status.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate.toISOString());
    if (params?.endDate) queryParams.append('endDate', params.endDate.toISOString());
    queryParams.append('page', (params?.page || 1).toString());
    queryParams.append('pageSize', (params?.pageSize || 20).toString());

    const response = await api.get<any>(`/alerts?${queryParams.toString()}`);
    
    // Enum değerlerini map et
    const mappedItems: Alert[] = (response.data.items || []).map((item: any) => ({
      ...item,
      alertType: mapAlertType(item.alertType),
      severity: mapAlertSeverity(item.severity),
      status: mapAlertStatus(item.status),
    }));
    
    return {
      items: mappedItems,
      totalCount: response.data.totalCount || 0,
      page: response.data.page || params?.page || 1,
      pageSize: response.data.pageSize || params?.pageSize || 20,
      totalPages: response.data.totalPages || 0,
    };
  },

  /**
   * Alert'i acknowledge et
   */
  async acknowledge(alertId: string): Promise<void> {
    await api.post(`/alerts/${alertId}/acknowledge`);
  },

  /**
   * Alert'i resolve et
   */
  async resolve(alertId: string, resolutionNotes?: string): Promise<void> {
    await api.post(`/alerts/${alertId}/resolve`, {
      resolutionNotes: resolutionNotes || null,
    });
  },
};

